/**
 * DomainHealthPage — Phase 8
 * Per-domain module coverage, skill-item gaps, and generate actions.
 * Route: /coach/workspace/:domain/health
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import CoachLayout from './CoachLayout';
import { getDomainHealth } from '../../services/api';
import api from '../../services/api';

const HEALTH_COLORS = {
  good:     { bg: 'rgba(34,197,94,0.15)',  border: 'rgba(34,197,94,0.4)',  text: '#4ade80' },
  warning:  { bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', text: '#fbbf24' },
  critical: { bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.4)',  text: '#f87171' },
};

function HealthBadge({ health, style }) {
  const c = HEALTH_COLORS[health] || HEALTH_COLORS.good;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700, background: c.bg, border: `1px solid ${c.border}`,
      color: c.text, textTransform: 'capitalize', ...style,
    }}>
      {health}
    </span>
  );
}

function CoveragePip({ count, threshold = 1 }) {
  if (count >= 5) return <span style={{ color: '#4ade80', fontWeight: 700 }}>✓</span>;
  if (count >= threshold) return <span style={{ color: '#fbbf24', fontWeight: 700 }}>⚠</span>;
  return <span style={{ color: '#f87171', fontWeight: 700 }}>✗</span>;
}

export default function DomainHealthPage() {
  const { domain } = useParams();
  const navigate = useNavigate();

  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [generateState, setGenerateState] = useState({}); // { [skillId]: 'navigating' }

  const fetchHealth = useCallback(() => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    getDomainHealth(domain)
      .then(({ data }) => setHealthData(data))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load domain health'))
      .finally(() => setLoading(false));
  }, [domain]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  // Navigate to Content Studio with a pre-filled prompt so the coach
  // can review and approve AI-generated items before they are saved.
  // Direct creation (placeholder stubs) is intentionally removed —
  // all generated content must go through the approval workflow.
  const handleGenerateItems = (skillId) => {
    setGenerateState((prev) => ({ ...prev, [skillId]: 'navigating' }));
    const skillLabel = skillId.split('.').pop().replace(/_/g, ' ');
    const domainLabel = domain.replace(/_/g, ' ');
    const prompt = encodeURIComponent(
      `Generate 5 medium-difficulty MCQ quiz items for the skill "${skillLabel}" ` +
      `in the "${domainLabel}" domain. ` +
      `Each item should have 4 answer choices (one correct), ` +
      `an explanation for why the correct answer is right and why the others are wrong, ` +
      `and an IRT difficulty rating between easy and hard. ` +
      `Include variety across conceptual, applied, and scenario-based question styles.`
    );
    navigate(`/coach/content-studio?domain=${encodeURIComponent(domain)}&artifact_type=cat_item&prompt=${prompt}`);
  };

  const handleGenerateCards = (skillId) => {
    setGenerateState((prev) => ({ ...prev, [`card_${skillId}`]: 'navigating' }));
    const skillLabel = skillId.split('.').pop().replace(/_/g, ' ');
    const domainLabel = domain.replace(/_/g, ' ');
    const prompt = encodeURIComponent(
      `Generate 5 flashcard review scenes for the skill "${skillLabel}" ` +
      `in the "${domainLabel}" domain. ` +
      `Each flashcard should have a clear front (question or key concept), ` +
      `a back (concise answer or explanation), and a key_insight summarising ` +
      `the most important thing the learner should remember. ` +
      `Vary the difficulty and focus on common misconceptions.`
    );
    navigate(`/coach/content-studio?domain=${encodeURIComponent(domain)}&artifact_type=flashcard&prompt=${prompt}`);
  };

  const modules = healthData?.modules || [];
  const skillsWithoutItems = healthData?.skills_without_items || [];
  const skillsWithoutCards = healthData?.skills_without_cards || [];
  const overall = healthData?.overall_health || 'good';

  return (
    <CoachLayout title={`Domain Health — ${domain}`}>
      <div data-testid="domain-health-page">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Domain</div>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#cbd5e1', textTransform: 'capitalize' }}>
              {domain?.replace(/_/g, ' ')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {healthData && <HealthBadge health={overall} style={{ fontSize: 13, padding: '4px 14px' }} />}
            <button className="btn btn-outline btn-sm" onClick={fetchHealth} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <Link className="btn btn-outline btn-sm" to={`/coach/curriculum/${domain}`}>
              Open Curriculum Builder
            </Link>
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
            borderRadius: 8, padding: '12px 16px', color: '#f87171', marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        {loading && <div className="coach-loading">Loading domain health…</div>}

        {/* Module coverage table */}
        {!loading && modules.length > 0 && (
          <div className="coach-section" style={{ margin: 0, marginBottom: 20 }}>
            <div className="coach-section-header">
              <h2 className="coach-section-title">Module Coverage</h2>
              <span style={{ fontSize: 12, color: '#64748b' }}>{modules.length} module{modules.length !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="coach-table" data-testid="module-coverage-table">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>Skills</th>
                    <th>Item Bank Coverage</th>
                    <th>Card Coverage</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {modules.map((mod) => {
                    const itemCounts = mod.item_counts_by_skill || {};
                    const totalItems = Object.values(itemCounts).reduce((a, b) => a + b, 0);
                    const skillsOk = Object.values(itemCounts).filter((c) => c >= 5).length;
                    const hasCoverage = mod.has_item_bank_coverage;

                    return (
                      <tr key={mod.module_id}>
                        <td style={{ fontWeight: 600, color: '#cbd5e1' }}>
                          {mod.title || mod.module_id}
                          <div style={{ fontSize: 10, color: '#475569', fontFamily: 'monospace' }}>
                            {mod.module_id}
                          </div>
                        </td>
                        <td style={{ color: '#94a3b8' }}>
                          {(mod.skill_ids || []).length}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CoveragePip count={skillsOk} threshold={1} />
                            <span style={{ fontSize: 12, color: '#64748b' }}>
                              {totalItems} item{totalItems !== 1 ? 's' : ''} · {skillsOk}/{(mod.skill_ids || []).length} skills OK
                            </span>
                          </div>
                        </td>
                        <td>
                          {hasCoverage ? (
                            <span style={{ color: '#4ade80', fontSize: 12 }}>Good</span>
                          ) : (
                            <span style={{ color: '#fbbf24', fontSize: 12 }}>Gaps present</span>
                          )}
                        </td>
                        <td>
                          <Link
                            to={`/coach/curriculum/${domain}`}
                            style={{ fontSize: 12, color: '#818cf8', textDecoration: 'none' }}
                          >
                            → Curriculum Builder
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Skills without items */}
        {!loading && skillsWithoutItems.length > 0 && (
          <div className="coach-section" style={{ margin: 0, marginBottom: 20 }}>
            <div className="coach-section-header">
              <h2 className="coach-section-title" style={{ color: '#f87171' }}>
                Skills Without Sufficient Items ({skillsWithoutItems.length})
              </h2>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
              These skills have fewer than 5 items in the item bank. Generate starter items or add them via the Item Bank editor.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {skillsWithoutItems.map((sid) => {
                const state = generateState[sid];
                return (
                  <div
                    key={sid}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.15)',
                      borderRadius: 6, padding: '8px 14px',
                    }}
                  >
                    <code style={{ fontSize: 12, color: '#fca5a5' }}>{sid}</code>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button
                        data-testid={`generate-items-btn-${sid}`}
                        className="btn btn-primary btn-sm"
                        style={{ fontSize: 11 }}
                        disabled={state === 'navigating'}
                        onClick={() => handleGenerateItems(sid)}
                      >
                        {state === 'navigating' ? 'Opening…' : '✨ Generate in Content Studio'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Skills without cards */}
        {!loading && skillsWithoutCards.length > 0 && (
          <div className="coach-section" style={{ margin: 0, marginBottom: 20 }}>
            <div className="coach-section-header">
              <h2 className="coach-section-title" style={{ color: '#fbbf24' }}>
                Skills Without Review Cards ({skillsWithoutCards.length})
              </h2>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
              These skills have no flashcard scenes. Generate starter cards or add them via the Review Card editor.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {skillsWithoutCards.map((sid) => {
                const state = generateState[`card_${sid}`];
                return (
                  <div
                    key={sid}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.15)',
                      borderRadius: 6, padding: '8px 14px',
                    }}
                  >
                    <code style={{ fontSize: 12, color: '#fde68a' }}>{sid}</code>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button
                        data-testid={`generate-cards-btn-${sid}`}
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: 11 }}
                        disabled={state === 'navigating'}
                        onClick={() => handleGenerateCards(sid)}
                      >
                        {state === 'navigating' ? 'Opening…' : '✨ Generate in Content Studio'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!loading && !error && modules.length === 0 && (
          <div className="coach-empty">
            <span className="coach-empty-icon">🗂️</span>
            <div className="coach-empty-title">No modules found</div>
            <div className="coach-empty-desc">
              Domain <strong>{domain}</strong> has no curriculum modules yet. Add modules in the{' '}
              <Link to={`/coach/curriculum/${domain}`} style={{ color: '#818cf8' }}>Curriculum Builder</Link>.
            </div>
          </div>
        )}
      </div>
    </CoachLayout>
  );
}
